import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarRange, Eye, EyeOff, Loader2, ReceiptText, RotateCcw, Save, ShieldCheck, Users, UserCog, UserRound } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner } from "../../components/common";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { api, errMsg, fmtDate, fmtDateTime } from "../../lib/api";
import { useRoleModules } from "../../lib/roleModules";
import { NoAccessPanel, ReadOnlyNotice } from "../../components/FeatureGate";
import { invalidateReceiptVisibility } from "../../lib/receiptVisibility";

const ROLE_ICONS = { citizen: UserRound, tax_collector: Users, supervisor: UserCog };
const DATE_KEYS = ["window_from", "window_to", "payment_from", "payment_to"];
const EMPTY_RULE = { enabled: true, window_from: "", window_to: "", payment_from: "", payment_to: "" };

const toForm = (roles) => Object.fromEntries(Object.entries(roles || {}).map(([r, v]) => [r, { enabled: v.enabled !== false, ...Object.fromEntries(DATE_KEYS.map((k) => [k, v[k] || ""])) }]));
const toBody = (form) => Object.fromEntries(Object.entries(form).map(([r, v]) => [r, { enabled: !!v.enabled, ...Object.fromEntries(DATE_KEYS.map((k) => [k, v[k] || null])) }]));

/** Live preview of one rule as the admin edits it (mirrors core.receipt_visibility.describe). */
export function effectOf(rule, today) {
  if (!rule) return { visible: true, text: "Visible" };
  if (!rule.enabled) return { visible: false, text: "Hidden — switched off" };
  const { window_from: wf, window_to: wt, payment_from: pf, payment_to: pt } = rule;
  let visible = true;
  const bits = [];
  if (wf || wt) {
    const inWindow = (!wf || today >= wf) && (!wt || today <= wt);
    const rng = `${wf ? fmtDate(wf) : "any date"} → ${wt ? fmtDate(wt) : "no end"}`;
    if (inWindow) bits.push(`Visible now (window ${rng})`);
    else if (wf && today < wf) { visible = false; bits.push(`Hidden until ${fmtDate(wf)} (window ${rng})`); }
    else { visible = false; bits.push(`Hidden — window ended ${fmtDate(wt)} (${rng})`); }
  } else bits.push("Visible");
  if (pf || pt) bits.push(`only receipts for payments dated ${pf ? fmtDate(pf) : "any date"} → ${pt ? fmtDate(pt) : "onwards"}`);
  return { visible, text: bits.join("; ") };
}

export const EffectBadge = ({ visible, testId }) => (
  visible
    ? <Badge variant="outline" className="gap-1 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30" data-testid={testId}><Eye className="h-3 w-3" /> Visible</Badge>
    : <Badge variant="outline" className="gap-1 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30" data-testid={testId}><EyeOff className="h-3 w-3" /> Hidden</Badge>
);

function RoleCard({ role, label, help, rule, today, onChange, readOnly }) {
  const Icon = ROLE_ICONS[role] || Users;
  const eff = effectOf(rule, today);
  const set = (k, v) => onChange({ ...rule, [k]: v });
  const winBad = rule.window_from && rule.window_to && rule.window_to < rule.window_from;
  const payBad = rule.payment_from && rule.payment_to && rule.payment_to < rule.payment_from;
  const dateField = (k, lbl) => (
    <div className="space-y-1">
      <Label htmlFor={`${role}-${k}`} className="text-xs text-muted-foreground">{lbl}</Label>
      <Input id={`${role}-${k}`} type="date" value={rule[k] || ""} onChange={(e) => set(k, e.target.value)} disabled={readOnly || !rule.enabled} className="h-9"
        data-testid={`rv-${role}-${k.replace("_", "-")}`} />
    </div>
  );
  return (
    <Card className={`shadow-sm ${rule.enabled ? "" : "border-dashed"}`} data-testid={`rv-role-card-${role}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="h-5 w-5" aria-hidden="true" /></div>
            <div className="min-w-0">
              <CardTitle className="text-base font-heading flex items-center gap-2">{label} <EffectBadge visible={eff.visible} testId={`rv-${role}-effect-badge`} /></CardTitle>
              <CardDescription className="text-xs mt-0.5">{help}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs font-medium" data-testid={`rv-${role}-toggle-label`}>{rule.enabled ? "ON" : "OFF"}</span>
            <Switch checked={!!rule.enabled} onCheckedChange={(v) => set("enabled", v)} disabled={readOnly} aria-label={`${label}: receipts visible`} data-testid={`rv-${role}-toggle`} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border p-3 space-y-2">
          <p className="text-sm font-medium flex items-center gap-2"><CalendarRange className="h-4 w-4 text-primary" aria-hidden="true" /> Visibility window (calendar dates)</p>
          <p className="text-xs text-muted-foreground">Receipts are visible only while today's date is between these dates. Leave both empty for no window.</p>
          <div className="grid grid-cols-2 gap-3">{dateField("window_from", "From")}{dateField("window_to", "To")}</div>
          {winBad && <p className="text-xs text-destructive" data-testid={`rv-${role}-window-error`}>"To" must not be before "From".</p>}
        </div>
        <div className="rounded-lg border p-3 space-y-2">
          <p className="text-sm font-medium flex items-center gap-2"><ReceiptText className="h-4 w-4 text-primary" aria-hidden="true" /> Payment-date filter</p>
          <p className="text-xs text-muted-foreground">Only receipts for payments made between these dates can be opened. Leave both empty to allow every payment date.</p>
          <div className="grid grid-cols-2 gap-3">{dateField("payment_from", "Payments from")}{dateField("payment_to", "Payments to")}</div>
          {payBad && <p className="text-xs text-destructive" data-testid={`rv-${role}-payment-error`}>"Payments to" must not be before "Payments from".</p>}
        </div>
        <p className="text-xs flex items-start gap-1.5 text-muted-foreground" data-testid={`rv-${role}-effect`}>
          <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" aria-hidden="true" />
          <span>Current effect: <span className="font-medium text-foreground">{eff.text}</span></span>
        </p>
      </CardContent>
    </Card>
  );
}

/** Phase 169 — Admin › Receipt Visibility: who may open payment receipts (citizens / collectors / supervisors) and for which dates. */
export default function ReceiptVisibility() {
  const { code } = useParams();
  const access = useRoleModules(code);
  const allowed = access.featureAllowed("settings", "receipt_visibility");
  const readOnly = !access.canWrite("settings", "receipt_visibility");
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = () => api.get(`/t/${code}/settings/receipt-visibility`).then((r) => { setData(r.data); setForm(toForm(r.data.roles)); setError(null); }).catch((e) => setError(errMsg(e)));
  useEffect(() => { if (allowed) load().catch(() => {}); }, [code, allowed]); // eslint-disable-line

  const dirty = useMemo(() => data && form && JSON.stringify(toForm(data.roles)) !== JSON.stringify(form), [data, form]);
  const invalid = useMemo(() => form && Object.values(form).some((r) => (r.window_from && r.window_to && r.window_to < r.window_from) || (r.payment_from && r.payment_to && r.payment_to < r.payment_from)), [form]);

  const save = async () => {
    if (invalid) { toast.error("Fix the date ranges first — a 'To' date is before its 'From' date"); return; }
    setBusy(true);
    try {
      const r = await api.put(`/t/${code}/settings/receipt-visibility`, toBody(form));
      setData(r.data); setForm(toForm(r.data.roles));
      invalidateReceiptVisibility(code);
      toast.success("Receipt visibility saved", { description: r.data.roles_order.map((k) => `${r.data.role_labels[k]}: ${r.data.state[k].effect}`).join(" · ") });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const resetAll = () => setForm(Object.fromEntries(Object.keys(form).map((r) => [r, { ...EMPTY_RULE }])));

  if (access.loaded && !allowed) return <div><PageHeader title="Receipt Visibility" /><NoAccessPanel what="Receipt Visibility" testId="rv-no-access" /></div>;
  if (error) return <div><PageHeader title="Receipt Visibility" /><Alert variant="destructive" data-testid="rv-error"><AlertTitle>Could not load the settings</AlertTitle><AlertDescription>{error} <Button size="sm" variant="outline" className="ml-2" onClick={load} data-testid="rv-retry">Retry</Button></AlertDescription></Alert></div>;
  if (!data || !form) return <Spinner />;

  const hiddenCount = data.roles_order.filter((k) => !effectOf(form[k], data.today).visible).length;
  return (
    <div data-testid="receipt-visibility-page">
      <PageHeader title="Receipt Visibility" subtitle="Decide which account types may open payment receipts, and between which dates. Payments and receipts are always recorded — these switches only control who can view, print or download them.">
        <div className="flex flex-wrap items-center gap-2">
          {readOnly && <ReadOnlyNotice what="receipt visibility settings" testId="rv-readonly-notice" />}
          <Button variant="ghost" size="sm" onClick={resetAll} disabled={readOnly || busy} data-testid="rv-reset-button"><RotateCcw className="h-4 w-4 mr-1.5" /> Reset to visible for all</Button>
          <Button onClick={save} disabled={readOnly || busy || !dirty || invalid} data-testid="rv-save-button">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save changes
          </Button>
        </div>
      </PageHeader>

      <Alert className="mb-4 border-primary/20 bg-primary/5" data-testid="rv-summary">
        <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
        <AlertTitle className="text-sm">
          {hiddenCount === 0 ? "Receipts are currently visible to every account type" : `Receipts are hidden for ${hiddenCount} of ${data.roles_order.length} account types right now`}
        </AlertTitle>
        <AlertDescription className="text-xs text-muted-foreground">
          Today is {fmtDate(data.today)} (IST). When a role is OFF — or today is outside its window — people in that role still see "Payment successful" with the amount and receipt number, but cannot open, print or download the receipt. Nothing is deleted; switching back ON restores access instantly.
          {data.updated_at && <> Last changed {fmtDateTime(data.updated_at)}{data.updated_by_name ? ` by ${data.updated_by_name}` : ""}.</>}
          {dirty && <span className="ml-1 font-medium text-foreground" data-testid="rv-unsaved">You have unsaved changes.</span>}
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4" data-testid="rv-role-cards">
        {data.roles_order.map((role) => (
          <RoleCard key={role} role={role} label={data.role_labels[role]} help={data.role_help[role]} rule={form[role]} today={data.today} readOnly={readOnly}
            onChange={(v) => setForm((f) => ({ ...f, [role]: v }))} />
        ))}
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        Municipal admins always see every receipt. Sub-users follow the rule of their parent role. Changes are logged in the <Link to={`/${code}/admin/audit-trail`} className="underline underline-offset-2">Audit Trail</Link>.
      </p>
    </div>
  );
}
